// XNGAGE CONFIDENTIAL
// __________________________
//
// Copyright (C) 2024 Xngage - All Rights Reserved
//
// All code or information contained herein is, and remains the
// property of Xngage LLC and its customers.  The intellectual
// and technical concepts contained are proprietary to Xngage LLC
// and may be covered by U.S. and Foreign Patents, patents in
// process, and are protected by trade secret or copyright law.
// Dissemination of this information or reproduction of this material
// is strictly forbidden unless prior written permission is obtained
// from Xngage LLC.
//

import StyledWrapper from "@insite/client-framework/Common/StyledWrapper";
import ApplicationState from "@insite/client-framework/Store/ApplicationState";
import { getSettingsCollection } from "@insite/client-framework/Store/Context/ContextSelectors";
import updateLoadParameter from "@insite/client-framework/Store/Pages/MyLists/Handlers/UpdateLoadParameter";
import translate from "@insite/client-framework/Translate";
import WidgetModule from "@insite/client-framework/Types/WidgetModule";
import WidgetProps from "@insite/client-framework/Types/WidgetProps";
import Select, { SelectPresentationProps } from "@insite/mobius/Select";
import InjectableCss from "@insite/mobius/utilities/InjectableCss";
import * as React from "react";
import { connect, ResolveThunks } from "react-redux";

const enum fields {
    defaultFilterOption = "defaultFilterOption",
}

interface OwnProps extends WidgetProps {
    fields: {
        [fields.defaultFilterOption]: "all" | "selectedCustomer";
    };
}

const mapStateToProps = (state: ApplicationState, ownProps: OwnProps) => {
    return {
        wishListSettings: getSettingsCollection(state).wishListSettings,
        forSelectedCustomer: (state.pages.myLists.getWishListsParameter as any)?.forSelectedCustomer,
    };
};

const mapDispatchToProps = {
    updateLoadParameter,
};

type Props = WidgetProps & ResolveThunks<typeof mapDispatchToProps> & ReturnType<typeof mapStateToProps>;

export interface MyListsCustomerFilterStyles {
    wrapper?: InjectableCss;
    select?: SelectPresentationProps;
}

export const myListsCustomerFilterStyles: MyListsCustomerFilterStyles = {};

const styles = myListsCustomerFilterStyles;

const MyListsCustomerFilter = (props: Props) => {
    const viewChangeHandler = (event: React.FormEvent<HTMLSelectElement>) => {
        props.updateLoadParameter({ forSelectedCustomer: event.currentTarget.value === "selectedCustomer" } as any);
    };

    React.useEffect(() => {
        const isSelectedCustomer = props.fields?.defaultFilterOption === "selectedCustomer";
        props.updateLoadParameter({ forSelectedCustomer: isSelectedCustomer } as any);
    }, []);

    return (
        <StyledWrapper {...styles.wrapper}>
            <Select
                {...styles.select}
                onChange={viewChangeHandler}
                data-test-selector="myListCustomerFilter"
                labelPosition="top"
                label={translate("For")}
                preventEllipsis={true}
                defaultValue={props.forSelectedCustomer ? "selectedCustomer" : props.fields?.defaultFilterOption || "all"}
                sizeVariant="default"
            >
                <option value="all">{translate("All Customers")}</option>
                <option value="selectedCustomer">{translate("For Selected Customer")}</option>
            </Select>
        </StyledWrapper>
    );
};

const widgetModule: WidgetModule = {
    component: connect(mapStateToProps, mapDispatchToProps)(MyListsCustomerFilter),
    definition: {
        group: "My Lists",
        displayName: "My Lists Customer Filter",
        allowedContexts: ["MyListsPage"],
        fieldDefinitions: [
            {
                name: fields.defaultFilterOption,
                displayName: "Default Filter Option",
                editorTemplate: "DropDownField",
                defaultValue: "selectedCustomer",
                fieldType: "General",
                options: [
                    { value: "all", displayName: "All Customers" },
                    { value: "selectedCustomer", displayName: "For Selected Customer" }
                ],
                isRequired: true
            }
        ]
    },
};

export default widgetModule;
